import { AppConstants, LEAD_STAGES } from '../constants/app.constants'
import { RUC_DISPLAY } from '@app/features/crm/constants/ruc.constants'
import {
    ISalesFunnelStageMetrics,
    ISalesFunnelKeyMetrics,
    ISalesFunnelStage,
    IStageLeakage,
    ICoreMetricItem,
    ILeadStatusMetric,
} from '../models/dashboard.model'
import { EStageId } from '../enum/sales-funnel.enum'
import { ICON_PATHS } from '../constants/assets.constants'
import { valueConstants } from '../constants/valueConstants'
import { REGEX_PATTERNS } from '../constants/regex.constants'

export class SalesFunnelHelper {
    private static readonly STAGE_ID_TO_DISPLAY_CODE: Readonly<Partial<Record<EStageId, string>>> =
        {
            [EStageId.DEAL_WON]: valueConstants.STAGE_CODE_WON,
            [EStageId.DROPPED]: valueConstants.STAGE_CODE_LOST,
            [EStageId.DEFERRED]: valueConstants.STAGE_CODE_HOLD,
        } as const

    static getStageDisplayCode(stageId: number): string {
        const overridden = this.STAGE_ID_TO_DISPLAY_CODE[stageId as EStageId]
        return overridden ?? `S${stageId}`
    }

    static getStageDisplayCodeFromCode(code: string): string {
        const match = code.match(REGEX_PATTERNS.SALES_FUNNEL_STAGE_CODE)
        if (!match) {
            return code
        }
        const stageId = Number(match[1])
        if (!Number.isFinite(stageId)) {
            return code
        }
        return this.getStageDisplayCode(stageId)
    }

    static formatCurrencyInLakhs(amount: number): string {
        if (amount === null || isNaN(amount)) {
            return valueConstants.NA
        }
        const lakhs = amount / AppConstants.LAKH_CONVERSION_CONSTANT
        return `${RUC_DISPLAY.CURRENCY_SYMBOL} ${lakhs.toFixed(1)}${RUC_DISPLAY.UNIT_LAKH_SUFFIX}`
    }

    static formatPercentage(value: number): string {
        if (value === null || value === undefined || isNaN(value)) {
            return valueConstants.NA
        }
        return `${value.toFixed(1)}%`
    }

    static getProgressColor(stageId: number): string {
        if (stageId === EStageId.DEAL_WON) {
            return '#66bb6a'
        }
        return '#E14F2E'
    }

    static transformStagesToFunnelFormat(
        stages: ISalesFunnelStageMetrics[],
        maxCount: number,
        isRucContext = false,
        showRevenueInDataView = false,
    ): ISalesFunnelStage[] {
        const filteredStages = isRucContext
            ? stages.filter(
                  (stage) =>
                      stage.stage_id >= EStageId.DEMO_COMPLETED &&
                      stage.stage_id <= EStageId.DEAL_WON,
              )
            : stages

        return filteredStages.map((stage) => {
            const isStage0 = stage.stage_id === EStageId.ALL_SCHOOLS
            const displayCount = stage.leads_count
            const rawProgressWidth = maxCount ? (displayCount / maxCount) * 100 : 0
            const progressWidth = displayCount ? Math.max(rawProgressWidth, 2) : 0
            const shouldShowRevenue =
                showRevenueInDataView ||
                (!isRucContext && stage.stage_id >= EStageId.DEMO_COMPLETED)
            const linkPayload =
                isStage0 && stage.client_ids?.length
                    ? { type: 'client' as const, ids: stage.client_ids }
                    : stage.lead_ids?.length
                      ? { type: 'lead' as const, ids: stage.lead_ids }
                      : undefined
            return {
                stageId: stage.stage_id,
                code: `S${stage.stage_id}`,
                displayCode: this.getStageDisplayCode(stage.stage_id),
                name: stage.stage_name,
                count: displayCount,
                progressColor: this.getProgressColor(stage.stage_id),
                progressWidth,
                conversion:
                    stage.conversion_rate !== null && stage.conversion_rate !== undefined
                        ? this.formatPercentage(stage.conversion_rate)
                        : valueConstants.NA,
                revenue:
                    shouldShowRevenue &&
                    stage.potential_revenue !== null &&
                    stage.potential_revenue !== undefined
                        ? this.formatCurrencyInLakhs(stage.potential_revenue)
                        : valueConstants.NA,
                dropRate:
                    stage.drop_rate !== null && stage.drop_rate !== undefined
                        ? this.formatPercentage(stage.drop_rate)
                        : valueConstants.NA,
                linkPayload,
            }
        })
    }

    static transformLeakages(
        keyMetrics: ISalesFunnelKeyMetrics,
        _stages: ISalesFunnelStageMetrics[],
    ): IStageLeakage[] {
        const s3Count = keyMetrics.leads_lost_in_s3 ?? 0
        const s4Count = keyMetrics.leads_lost_in_s4 ?? 0
        const s5Count = keyMetrics.leads_lost_in_s5 ?? 0
        const s6Count = keyMetrics.leads_lost_in_s6 ?? 0

        const leakages: IStageLeakage[] = []

        leakages.push({
            count: s3Count,
            fromStage: this.getStageDisplayCode(EStageId.DEMO_COMPLETED),
            toStage: this.getStageDisplayCode(EStageId.PROPOSAL_SUBMITTED),
            revenueLost: `${this.formatCurrencyInLakhs(keyMetrics.revenue_leakage_in_s3 || 0)} Lost`,
            leadIds: keyMetrics.leads_lost_in_s3_lead_ids,
        })

        leakages.push({
            count: s4Count,
            fromStage: this.getStageDisplayCode(EStageId.PROPOSAL_SUBMITTED),
            toStage: this.getStageDisplayCode(EStageId.CONTRACT_NEGOTIATION),
            revenueLost: `${this.formatCurrencyInLakhs(keyMetrics.revenue_leakage_in_s4 || 0)} Lost`,
            leadIds: keyMetrics.leads_lost_in_s4_lead_ids,
        })

        leakages.push({
            count: s5Count,
            fromStage: this.getStageDisplayCode(EStageId.CONTRACT_NEGOTIATION),
            toStage: this.getStageDisplayCode(EStageId.SSF_SIGNED),
            revenueLost: `${this.formatCurrencyInLakhs(keyMetrics.revenue_leakage_in_s5 || 0)} Lost`,
            leadIds: keyMetrics.leads_lost_in_s5_lead_ids,
        })

        leakages.push({
            count: s6Count,
            fromStage: this.getStageDisplayCode(EStageId.SSF_SIGNED),
            toStage: this.getStageDisplayCode(EStageId.DEAL_WON),
            revenueLost: `${this.formatCurrencyInLakhs(keyMetrics.revenue_leakage_in_s6 || 0)} Lost`,
            leadIds: keyMetrics.leads_lost_in_s6_lead_ids,
        })

        return leakages
    }

    static calculateConversionRate(wonCount: number, beginningCount: number): string {
        if (!beginningCount) {
            return '0%'
        }
        const rate = (wonCount / beginningCount) * 100
        return this.formatPercentage(rate)
    }

    static calculateDropoutRate(droppedCount: number, beginningCount: number): string {
        if (beginningCount === 0) {
            return '0%'
        }
        const rate = (droppedCount / beginningCount) * 100
        return this.formatPercentage(rate)
    }

    static transformToCoreMetrics(
        stages: ISalesFunnelStageMetrics[],
        newSchoolsWonCount: number,
        leadStatusMetrics: ILeadStatusMetric[] = [],
        keyMetrics?: ISalesFunnelKeyMetrics | null,
        isTeamView = false,
    ): ICoreMetricItem[] {
        const stage0 = stages.find((stageDetail) => stageDetail.stage_id === EStageId.ALL_SCHOOLS)
        const stage1 = stages.find((stageDetail) => stageDetail.stage_id === EStageId.PROSPECTING)
        const stage2 = stages.find(
            (stageDetail) => stageDetail.stage_id === EStageId.PROSPECT_IDENTIFIED,
        )
        const stage3 = stages.find(
            (stageDetail) => stageDetail.stage_id === EStageId.DEMO_COMPLETED,
        )
        const stage4 = stages.find(
            (stageDetail) => stageDetail.stage_id === EStageId.PROPOSAL_SUBMITTED,
        )
        const stage5 = stages.find(
            (stageDetail) => stageDetail.stage_id === EStageId.CONTRACT_NEGOTIATION,
        )
        const stage6 = stages.find((stageDetail) => stageDetail.stage_id === EStageId.SSF_SIGNED)
        const stage7 = stages.find((stageDetail) => stageDetail.stage_id === EStageId.DEAL_WON)
        const droppedStageMetrics = stages.find(
            (stageDetail) => stageDetail.stage_id === EStageId.DROPPED,
        )
        const deferredStageMetrics = stages.find(
            (stageDetail) => stageDetail.stage_id === EStageId.DEFERRED,
        )

        const totalSchools = isTeamView
            ? (stage0?.leads_count ?? stage0?.client_ids?.length ?? 0)
            : ((stage0?.client_ids?.length != null ? stage0.client_ids.length : undefined) ??
              stage0?.leads_count ??
              0)
        const distinctSchoolsTooltip = isTeamView
            ? 'The total number of schools assigned to me and my team.'
            : 'The total number of schools assigned to me.'
        const leadProspecting = stage1?.leads_count || 0
        // Leads Pursued = S1 and progressed beyond S1 (S1 through S7)
        const leadsPursuedLeadIds = [stage1, stage2, stage3, stage4, stage5, stage6, stage7].reduce<
            number[]
        >((acc, stage) => (stage?.lead_ids ? [...acc, ...stage.lead_ids] : acc), [])
        const uniqueLeadsPursuedLeadIds = [...new Set(leadsPursuedLeadIds)]
        const activePipeline = Math.max(0, (stage3?.leads_count || 0) - (stage7?.leads_count || 0))
        const activePipelineLeadIds = [stage3, stage4, stage5, stage6].reduce<number[]>(
            (acc, stage) => (stage?.lead_ids ? [...acc, ...stage.lead_ids] : acc),
            [],
        )
        const uniqueActivePipelineLeadIds = [...new Set(activePipelineLeadIds)]
        const leadWon = stage7?.leads_count || 0
        const lostStatusMetric = leadStatusMetrics.find(
            (metric) => metric.lead_status === LEAD_STAGES.LEAD_LOST,
        )
        const deferredStatusMetric = leadStatusMetrics.find(
            (metric) => metric.lead_status === LEAD_STAGES.LEAD_DEFERRED,
        )
        const leadDropped = droppedStageMetrics?.leads_count ?? lostStatusMetric?.leads_count ?? 0
        const leadDeferred =
            deferredStageMetrics?.leads_count ?? deferredStatusMetric?.leads_count ?? 0
        const leadIdsForLostLeadsLink = droppedStageMetrics?.lead_ids?.length
            ? droppedStageMetrics.lead_ids
            : lostStatusMetric?.lead_ids
        const leadIdsForDeferredLeadsLink = deferredStageMetrics?.lead_ids?.length
            ? deferredStageMetrics.lead_ids
            : deferredStatusMetric?.lead_ids

        const beginningCount = stage1?.leads_count || 0
        const conversionRate = this.calculateConversionRate(leadWon, beginningCount)
        const dropoutRate = this.calculateDropoutRate(leadDropped, beginningCount)

        return [
            {
                label: 'Distinct Schools Assigned',
                value: totalSchools.toString(),
                iconPath: ICON_PATHS.SCHOOL,
                tooltip: distinctSchoolsTooltip,
                linkPayload: stage0?.client_ids?.length
                    ? { type: 'client', ids: stage0.client_ids }
                    : undefined,
            },
            {
                label: 'Leads Pursued',
                value: leadProspecting.toString(),
                iconPath: ICON_PATHS.CONTACT_PERSON,
                tooltip: 'Total leads in S1 or progressed beyond S1.',
                linkPayload:
                    uniqueLeadsPursuedLeadIds.length > 0
                        ? { type: 'lead', ids: uniqueLeadsPursuedLeadIds }
                        : undefined,
            },
            {
                label: 'Active Pipeline',
                value: activePipeline.toString(),
                iconPath: ICON_PATHS.SCHOOL,
                tooltip:
                    'All leads that are currently in stages S3 through S6 (inclusive of both S3 and S6).',
                linkPayload:
                    uniqueActivePipelineLeadIds.length > 0
                        ? { type: 'lead', ids: uniqueActivePipelineLeadIds }
                        : undefined,
            },
            {
                label: 'Leads Won',
                value: leadWon.toString(),
                iconPath: ICON_PATHS.CHECK_CIRCLE,
                tooltip: 'Successfully closed deals that resulted in revenue',
                linkPayload: stage7?.lead_ids?.length
                    ? { type: 'lead', ids: stage7.lead_ids }
                    : undefined,
            },
            {
                label: 'Leads Lost',
                value: leadDropped.toString(),
                iconPath: ICON_PATHS.CLOSE,
                tooltip:
                    'The number of leads from S3 onwards that did not convert and were marked as lost.',
                linkPayload: leadIdsForLostLeadsLink?.length
                    ? { type: 'lead', ids: leadIdsForLostLeadsLink }
                    : undefined,
            },
            {
                label: 'Deferred',
                value: leadDeferred.toString(),
                iconPath: ICON_PATHS.ALERT_CIRCLE,
                tooltip: 'The number of leads that have been deferred.',
                linkPayload: leadIdsForDeferredLeadsLink?.length
                    ? { type: 'lead', ids: leadIdsForDeferredLeadsLink }
                    : undefined,
            },
            {
                label: 'Conversion Rate',
                value: conversionRate,
                iconPath: ICON_PATHS.TRENDING_UP,
                isPercentage: true,
                tooltip:
                    'The percentage of leads created at S1 that successfully converted into Won Deals (SW).',
            },
            {
                label: 'Drop-off Rate',
                value: dropoutRate,
                iconPath: ICON_PATHS.ALERT_CIRCLE,
                isPercentage: true,
                tooltip:
                    'The percentage of deals that exited the pipeline without conversion, starting from S3 onwards.',
            },
            {
                label: 'New Schools Won',
                value: (newSchoolsWonCount || 0).toString(),
                iconPath: ICON_PATHS.CHECK_CIRCLE,
                tooltip: 'Number of new schools that have been successfully won.',
                linkPayload: keyMetrics?.new_schools_won_customer_ids?.length
                    ? { type: 'client', ids: keyMetrics.new_schools_won_customer_ids }
                    : undefined,
            },
        ]
    }
}
